/**
 * Session + view-capability access for the SPA.
 *
 * This replaces `session.server.ts`, which used `next/headers` to forward the
 * browser's cookies from a server render. There is no server render now: the
 * browser holds the cookie itself and sends it on every cross-origin call
 * (see `apiFetch`). What the server layouts did in a blocking `await` before
 * painting, the router now does in `beforeLoad`/`loader` — so the guard still
 * resolves *before* a protected route renders, and no surface flashes for an
 * account that isn't allowed to see it.
 *
 * The per-page prefetch helpers from `session.server.ts` (getServerAlerts,
 * getServerEmployee, getServerDocument, …) are deliberately not reproduced.
 * Each one existed to avoid a loading flash on first paint; the route loaders
 * now call the same `*-api.ts` client through React Query's `ensureQueryData`,
 * which serves that purpose and keeps one fetch path per resource instead of a
 * server copy and a client copy that can drift.
 */
import type { QueryClient } from "@tanstack/react-query";

import { apiFetch, apiUrl } from "./api";
import { servedViews } from "./surfaces";
// Web-only, like tab sync itself: admin's copy of this file doesn't call it.
import { announceAccountChange } from "./tab-sync";
import { localView, VIEW_COOKIE, type ViewCapabilities, type ViewMode } from "./view-mode";

export type SessionUser = {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  role?: string | null;
  emailVerified?: boolean;
  twoFactorEnabled?: boolean | null;
  createdAt?: string;
};

export type Session = {
  id: string;
  userId: string;
  expiresAt: string;
  activeOrganizationId?: string | null;
  impersonatedBy?: string | null;
};

export type SessionPayload = {
  user: SessionUser;
  session: Session;
};

/** Retained under the old name so ported components keep type-checking. */
export type ServerSessionPayload = SessionPayload;
export type ServerSessionUser = SessionUser;
export type ServerSession = Session;

export const NO_CAPABILITIES: ViewCapabilities = {
  isAdmin: false,
  isModerator: false,
  moderatorSections: [],
  isEmployeeRole: false,
  hasEmployeeProfile: false,
  hasWorkspaceMembership: false,
  memberships: [],
};

/** `null` when there is no valid session — callers handle the redirect. */
export async function fetchSession(): Promise<SessionPayload | null> {
  try {
    const res = await apiFetch(apiUrl("/api/auth/get-session"), {
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as SessionPayload | null;
    return data?.user ? data : null;
  } catch (err) {
    console.error("[session] backend unreachable", err);
    return null;
  }
}

/**
 * The account's switchable-surface capabilities. Falls back to "no extra
 * capabilities" when the backend is unreachable, matching the server version:
 * layouts then degrade to the plain role default rather than crashing.
 */
export async function fetchViewCapabilities(): Promise<ViewCapabilities> {
  try {
    const res = await apiFetch(apiUrl("/api/account/views"), {
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) return NO_CAPABILITIES;
    return (await res.json()) as ViewCapabilities;
  } catch (err) {
    console.error("[views] backend unreachable", err);
    return NO_CAPABILITIES;
  }
}

export function readViewCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${VIEW_COOKIE}=`))
    ?.split("=")[1];
}

/**
 * The account's active surface *on the app that is running*, or `null` when
 * this app hosts no surface the account may use.
 *
 * Scoped to `servedViews()` on purpose: signing in on one app must never move
 * the account to the other. Within that scope the resolution is unchanged — the
 * `hr_view` cookie when the account is actually allowed it, else the role
 * default — so the cookie stays a preference and never a privilege.
 */
export function activeView(capabilities: ViewCapabilities): ViewMode | null {
  return localView(readViewCookie(), capabilities, servedViews());
}

/* ---------------------------------------------------------------------- *
 * Query options — used by route loaders so the guard and the components
 * that later read the same data share one cache entry and one request.
 * ---------------------------------------------------------------------- */

// `gcTime: Infinity` because nothing subscribes to these with `useQuery` — the
// guards read them imperatively. With the default five-minute GC, the first
// click after a short idle found the cache empty and paid a blocking round trip
// in `beforeLoad` before the page could change.
export const sessionQuery = {
  queryKey: ["session"] as const,
  queryFn: fetchSession,
  staleTime: 30_000,
  gcTime: Number.POSITIVE_INFINITY,
};

export const viewCapabilitiesQuery = {
  queryKey: ["view-capabilities"] as const,
  queryFn: fetchViewCapabilities,
  staleTime: 30_000,
  gcTime: Number.POSITIVE_INFINITY,
};

export type AuthContext = {
  session: SessionPayload;
  capabilities: ViewCapabilities;
  /** `null` when this app serves no surface the account is allowed. */
  view: ViewMode | null;
};

/** An {@link AuthContext} whose surface this app can actually render. */
export type LocalAuthContext = AuthContext & { view: ViewMode };

/**
 * Loads session + capabilities for a route guard. Returns `null` when there is
 * no session, so the caller can throw the redirect with its own sign-in path
 * (the two apps each host their own auth routes).
 */
export async function loadAuthContext(queryClient: QueryClient): Promise<AuthContext | null> {
  // `revalidateIfStale` answers from the cache at once and refreshes in the
  // background once the entry is older than its `staleTime`, so a navigation
  // never waits on the network but a revoked session is still noticed.
  const [session, capabilities] = await Promise.all([
    queryClient.ensureQueryData({ ...sessionQuery, revalidateIfStale: true }),
    queryClient.ensureQueryData({ ...viewCapabilitiesQuery, revalidateIfStale: true }),
  ]);
  if (!session) return null;
  return { session, capabilities, view: activeView(capabilities) };
}

/**
 * Drops the cached session and capabilities so the next guard refetches them.
 *
 * Both queries carry a `staleTime`, and `ensureQueryData` serves a cached entry
 * without revalidating — so after a sign-in the guards would otherwise keep
 * reading the "signed out" answer that the auth screen's own guard cached.
 */
export function resetAuthCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: sessionQuery.queryKey });
  queryClient.removeQueries({ queryKey: viewCapabilitiesQuery.queryKey });
}

/**
 * Drops everything this browser holds for the account that is leaving. Call it
 * on sign-out, before navigating away from the authenticated surface.
 *
 * Two things go wrong without it, and neither is visible until you look at the
 * cache rather than the cookie:
 *
 *  - The guards never re-ask. They read the session through `ensureQueryData`,
 *    which serves a cached entry without revalidating, so `signOut()` clearing
 *    the cookie server-side changes nothing here for another 30 seconds.
 *    `requireView` keeps passing, and `redirectIfSignedIn` — the guard on the
 *    sign-in screen itself — reads "signed in" and bounces the user straight
 *    back to the surface they just left. The click looks like it did nothing,
 *    and only a full reload, which rebuilds the query client from scratch,
 *    appears to work.
 *  - Everything else in the cache is account-scoped as well: the company, the
 *    plan, employee lists, documents. `resetAuthCache` is the right tool
 *    between two states of the *same* account and the wrong one here, because
 *    it would leave all of that for whoever signs in next on this browser to
 *    see before their own data lands.
 *
 * Hence `clear()` rather than a set of `removeQueries` calls: the safe default
 * on the way out is that nothing survives.
 */
export function clearAccountCache(queryClient: QueryClient): void {
  queryClient.clear();
  announceAccountChange();
}

/**
 * Records a sign-out in the cache *without* tearing down the surface that is
 * still on screen.
 *
 * Calling {@link clearAccountCache} before leaving emptied every query the
 * mounted dashboard was subscribed to, so for the length of the navigation it
 * re-rendered as skeletons (and refetched, unauthenticated) — the dashboard
 * outline flashed on the way to the sign-in form. Seeding a signed-out session
 * instead lets the `/sign-in` guard resolve synchronously, and the caller clears
 * the rest once the old surface has unmounted.
 */
export function markSignedOut(queryClient: QueryClient): void {
  queryClient.setQueryData(sessionQuery.queryKey, null);
}

/**
 * Drops every cached answer that belongs to the active workspace, for when the
 * workspace itself changes (a company switch, a new company, an accepted
 * invite).
 *
 * The cookie moves server-side, but the query keys are not scoped by company,
 * so every entry — the company, the dashboard counters, the employee lists, and
 * the session holding `activeOrganizationId` — still holds the previous
 * company's answer. `invalidateQueries` would keep showing that answer until
 * the refetch lands, and loaders reading through `ensureQueryData` would keep
 * serving it; a reset empties them, so the next reader fetches and mounted
 * components show a loading state instead of the wrong company's data.
 */
export function resetWorkspaceCache(queryClient: QueryClient): Promise<void> {
  resetAuthCache(queryClient);
  announceAccountChange();
  return queryClient.resetQueries();
}

export function isAdmin(session: SessionPayload | null): boolean {
  return session?.user?.role === "admin";
}

/**
 * Whether the session belongs to a self-service portal employee (not an HR
 * user). Employees are gated to `/portal` and kept out of the HR dashboard.
 */
export function isEmployee(session: SessionPayload | null): boolean {
  return session?.user?.role === "employee";
}
